import React, { useState, useEffect } from 'react';

import { Link } from 'react-router-dom';
import { Button, Grid, Container, Typography } from '@mui/material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import RestUtilities from '../services/RestUtilities';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  artistState,
  loadingState,
  toastState,
  youTubeVideosState,
} from '../state/RecoilState';
import { YouTubeVideo } from '../@types/api';
import MustPickArtist from '../components/Common/MustPickArtist';
import ModalFileManager from '../components/Common/ModalFileManager';
import { uploadType } from '../Constants';
import YouTubeVideoCard from '../components/YouTubeVideoCard';

const sortVideos = (a: YouTubeVideo, b: YouTubeVideo) => {
  if (a.isLive < b.isLive) {
    return 1;
  } else if (a.isLive > b.isLive) {
    return -1;
  } else if (a.orderIndex > b.orderIndex) {
    return 1;
  } else if (a.orderIndex < b.orderIndex) {
    return -1;
  }
  return 0;
};

const YouTube = () => {
  const artist = useRecoilValue(artistState);
  const setLoading = useSetRecoilState(loadingState);
  const setToast = useSetRecoilState(toastState);
  const [youTubeVideos, setYouTubeVideos] = useRecoilState<
    YouTubeVideo[] | undefined
  >(youTubeVideosState);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [show, setShow] = useState<boolean>(false);
  const fileType = uploadType.youtube;

  useEffect(() => {
    const fetchVideos = async (artistId: number) => {
      setLoading(true);
      const url = `api/YouTubeVideo/${artistId}`;
      const response = await RestUtilities.get(url);
      if (response.ok) {
        setYouTubeVideos(response.content);
        setLoading(false);
      }
    };
    if (artist && !youTubeVideos) {
      fetchVideos(artist.artistId);
    }
  }, [artist, youTubeVideos, setLoading, setYouTubeVideos]);

  const onSaveVideo = async (video: YouTubeVideo) => {
    await RestUtilities.put(`api/Video/${video.id}/${artist?.artistId}`, video);
    setToast('Video saved');
    if (youTubeVideos) {
      const newVideos = youTubeVideos.filter((x) => x.id !== video.id);
      newVideos.push(video);
      setYouTubeVideos(newVideos);
    }
  };

  const onReady = (event: any) => {
    // access to player in all event handlers via event.target
    event.target.pauseVideo();
  };

  const renderVideos = () => {
    if (!youTubeVideos) return null;
    const sortedVideos = youTubeVideos.toSorted(sortVideos);
    return sortedVideos.map((x: YouTubeVideo) => {
      return (
        <Grid
          key={x.id}
          item
          lg={3}
          md={4}
          sm={6}
          onClick={(e) => onItemSelect(e, x)}
          className='item-container'
        >
          <YouTubeVideoCard
            video={x}
            isSelected={x.id === selectedId}
            onSaveVideo={onSaveVideo}
            onReady={onReady}
          />
        </Grid>
      );
    });
  };

  const onItemSelect = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    item: YouTubeVideo
  ) => {
    if (selectedId !== item.id) {
      e.preventDefault();
      setSelectedId(item.id);
    }
  };

  const handleUploadSelect = async (item: any) => {
    if (!youTubeVideos) {
      setShow(false);
      return;
    } else {
      var youTubeVideo = {
        uploadId: item.uploadId,
        artistId: artist?.artistId,
        isLive: false,
        orderIndex: 99,
      };
      const response = await RestUtilities.post(
        `api/YouTubeVideo/${artist?.artistId}`,
        youTubeVideo
      );
      if (response.ok) {
        setYouTubeVideos([...youTubeVideos, response.content]);
      }
      setShow(false);
    }
  };

  if (!artist) {
    return <MustPickArtist />;
  }

  return (
    <Container maxWidth='lg' className='YouTubeVideos'>
      <Typography variant='h4' gutterBottom>
        {artist.artistName} YouTube Videos
      </Typography>
      <Typography variant='body2'>
        This is the current youtube videos appearing on your band's website. You
        can reorder and hide videos You can edit youtube ID and title in the{' '}
        <Link to={'/files'}>File Editor</Link>.
      </Typography>

      {youTubeVideos && (
        <>
          <Grid container spacing={2} className='videos-row'>
            <Grid item xs={12}>
              <Button
                variant='contained'
                color='primary'
                endIcon={<YouTubeIcon />}
                size='small'
                onClick={() => {
                  setShow(true);
                }}
                sx={{ my: 2 }}
              >
                Add new YouTube video
              </Button>
            </Grid>
            {renderVideos()}
          </Grid>
        </>
      )}
      <ModalFileManager
        show={show}
        handleClose={() => setShow(false)}
        fileType={fileType}
        handleUploadSelect={handleUploadSelect}
      />
    </Container>
  );
};

export default YouTube;
